import { Card, CardContent, Theme, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Alert from '@material-ui/lab/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import React, { Suspense, lazy, useContext, useEffect, useState, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Throbber } from '.';
import { ErrorContext } from '../context/ErrorContext';
import { useQueryStringParams } from '../hooks/useQueryStringParams';

import * as api from '../store/api-client';
import { acceptInvitation } from '../store/api-client';
import { useAppSelector } from '../store/hooks';
import { Organization } from '../store/models';

import { _location } from '../store/slices/locationSlice';
import { _organization } from '../store/slices/organizationSlice';
import loadingGif from '../theme/viiision_loop.gif';
import { colors } from '../theme/palette';
import { BlockingModal } from './BlockingModal';
import { InfoLink } from './InfoLink';
import { viiisionFailure } from './ViiisionFailureSnackbar';

// lazy loading this because it's large
const PasswordStrengthBar = lazy(() => import('react-password-strength-bar'));

export type Props = {
	onboardingRoute: string;
	organizationId: string;
	referrer?: string;
};

type FormValues = {
	firstName: string;
	lastName: string;
	orgName: string;
	email: string;
	password: string;
	password2: string;
	confirmationCode: string;
	termsOfService: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
	form: {
		display: 'flex',
		flexGrow: 1,
	},
	fillHeight: {
		flexGrow: 1,
	},
	centerMyContentPlease: {
		textAlign: 'center',
	},
	topOfForm: {
		height: 'min-content',
	},
	bottomOfForm: {
		flexGrow: 1,
		paddingTop: 20,
		alignItems: 'flex-end',
	},
	horizontalLine: {
		border: `1px solid ${colors.graySecondaryLight}`,
	},
	orSignInWithText: {
		lineHeight: 1,
	},
	verifyButton: {
		marginTop: 20,
	},
	pageTitle: {
		paddingBottom: theme.spacing(4),
	},
	paperTitle: {
		color: colors.grayDark,
		paddingBottom: 8,
		paddingLeft: 10,
	},
	paper: {
		paddingLeft: 20,
	},
	passwordRoot: {
		position: 'relative',
	},
	passwordRequirement: {
		position: 'absolute',
		top: 100,
		right: 0,
		left: 0,
		zIndex: 1,
		minWidth: 380,
		padding: 10,
		borderRadius: 10,
		backgroundColor: colors.pureWhite,
		boxShadow: '0px 1px 4px rgba(24, 27, 32, 0.2)',
	},
	img1: {
		marginBottom: '-100px',
		// position: 'relative',
		// left: 0,
		// top: 0,
		// marginTop: 'auto',
		// marginBottom: 'auto',
		// width: 'auto',
	},
	loading_buffer: {
		left: '-10%',
		paddingLeft: 'auto',
		paddingRight: 'auto',
		marginLeft: 'auto',
		marginRight: 'auto',
		width: '120%',
	},
}));

export const SignUpForm = ({ onboardingRoute, organizationId, referrer }: Props) => {
	const { handleError } = useContext(ErrorContext);
	const [errorMessage, setErrorMessage] = useState('');
	const [showLoading, setShowLoading] = useState(false);
	const isMountedRef = useRef(true);
	const history = useHistory();
	const classes = useStyles();
	const urlParams = useQueryStringParams();
	const organization: Organization = useAppSelector(_organization);
	const location = useAppSelector(_location);

	const [open, setOpen] = React.useState(false);

	const invitationId = urlParams.get('invitationId');

	const { register, errors, formState, getValues, handleSubmit, watch } = useForm({
		mode: 'onChange',
		defaultValues: {
			firstName: 'rFirstName',
			lastName: 'rLastName',
			orgName: '',
			email: '',
			password: '',
			password2: '',
			confirmationCode: '',
			termsOfService: true,
		},
	});
	watch(['password', 'orgName']);

	// Cleanup effect to prevent state updates on unmounted component
	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// useEffect to send a GA4 event when the user lands on the sign up page
	useEffect(() => {
		// GOOGLE ANALYTICS
		window.globalThis.gtag('event', 'sign_up_landing', {
			value: 1,
		});
	}, []);

	// useEffect to detect if a location is set in Redux, and if so, hard referesh the page to clear the store out
	useEffect(() => {
		if (location) {
			window.location.reload();
		}
	}, [location]);

	// handle when form is submitted
	const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
		data.firstName = data.firstName || 'rFirstName';
		data.lastName = data.lastName || 'rLastName';
		data.orgName = data.orgName.trim() || `${Date.now().toString().slice()}`;
		//show the loading pop-up
		if (isMountedRef.current) {
			setShowLoading(true);
		}

		try {
			// create a viiision organization via PHP
			let org = await api.createNewOrganization({
				name: data.orgName.trim() || `${Date.now().toString().slice()}`,
				email: data.email,
				password: data.password,
			});

			// set the org in redux by dispatching action
			// if (isMountedRef.current) {
			// 	setOrg(org);
			// }

			// // if we dont get an org, close the modal and pop an error
			if (!org.id) {
				if (isMountedRef.current) {
					setShowLoading(false);
					viiisionFailure('Error creating organization.');
					setErrorMessage('Error creating organization.');
				}
				return;
			}

			// GOOGLE ANALYTICS
			window.globalThis.gtag('event', 'Sign_up', {
				referrer: referrer,
				category: 'Sign_up',
				action: 'Sign_up',
				value: 1,
				// first_name: data.firstName,
				// last_name: data.lastName,
				// organization_name: data.orgName,
				// email: data.email,
				// label: data.email,
			});

			history.push('/essentials/confirm-email?email=' + encodeURIComponent(data.email));
		} catch (error) {
			// if anything throws an unexpected error, we safely close the modal and assume all is lost.
			if (isMountedRef.current) {
				setShowLoading(false);
				handleError(JSON.stringify(error));
				setErrorMessage(JSON.stringify(error));
			}
			console.error('error cloning template app for new organization', error);
			// TODO: if something fails here, we need to clean up.
		}
	};

	const validationMessages = {
		requiredMessage: 'This field is required',
		passwordLength: 'Your password must be at least 8 characters',
		passwordsMatch: 'Password fields must match',
		emailContainsSlash:
			'Email must not contain a "/" for security reasons. Contact services@viiision.com if this affects you.',
		orgNameContainsUnsupportedCharacters: 'Organization name should not contain special characters',
		orgNameContainsUnsupportedChars: 'Organization name contains unsupported characters.',
	};

	// useEffect to accept invitation
	useEffect(() => {
		// if the user is invited by an organization, accept the invitation
		if (invitationId && organization) {
			acceptInvitation(invitationId, organization?.id);
		}
	}, [invitationId, organization]);

	// REGEX for:
	// Contains a minimum of 8 characters
	// Contains at least one lowercase letter(a–z)
	// Contains at least one uppercase letter(A–Z)
	// Contains at least one number(0–9)
	// Contains at least one special character(!@#$ %)
	const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%])[A-Za-z\d!@#$%]{8,}$/; //Dont worry about understanding this, but ask if you're curious

	// function to validate against Regex
	const validatePassword = (data: string) => {
		if (!data) {
			return 'Password is required';
		}

		if (!data.match(passwordRegex)) {
			// return "Password must contain at least 8 characters, one lowercase letter, one uppercase letter, one number, and one special character (!@#$%)";
			setOpen(true);
			return 'The password requirements are not met.';
		}
		setOpen(false);
		return undefined;
	};

	return (
		<>
			<BlockingModal open={showLoading} fullPage={false}>
				<Card>
					<CardContent>
						<Grid container justifyContent="center">
							<Grid
								item
								xs={12}
								style={{ display: 'flex', marginBottom: 16 }}
								className={classes.centerMyContentPlease}
							>
								<Typography variant="h2">
									<p>Your account is being created</p>
									<img src={loadingGif} alt="logo" className={classes.img1} />
									<p>It should be ready in 30 seconds!</p>
									<LinearProgress variant="query" className={classes.loading_buffer} />
								</Typography>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			</BlockingModal>

			<Typography variant="h5" align="center" className={classes.pageTitle}>
				Welcome to Viiision!
			</Typography>
			<form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
				<Grid container direction="row" className={classes.fillHeight}>
					<Grid container item direction="row" xs={12} spacing={2} className={classes.topOfForm}>
						<Grid item xs={12}>
							{errorMessage && <Alert severity="error">{errorMessage}</Alert>}
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Organization Name"
								fullWidth
								variant="outlined"
								type="text"
								name="orgName"
								error={!!errors.orgName}
								helperText={errors?.orgName?.message}
								inputRef={register({
									required: validationMessages.requiredMessage,
								})}
							/>
						</Grid>

						<Grid item xs={12}>
							<TextField
								label="Email"
								fullWidth
								variant="outlined"
								type="email"
								name="email"
								// value={emailInUrl || ''}
								error={!!errors.email}
								helperText={errors?.email?.message}
								inputRef={register({
									required: validationMessages.requiredMessage,
									validate: (data) => {
										if (data.includes('/')) {
											return validationMessages.emailContainsSlash;
										}
										return undefined;
									},
								})}
							/>
						</Grid>
						<Grid container item spacing={2}>
							<Grid item xs={12} sm={6}>
								<ClickAwayListener
									mouseEvent="onMouseDown"
									touchEvent="onTouchStart"
									onClickAway={() => setOpen(false)}
								>
									<div className={classes.passwordRoot}>
										<TextField
											label="Password"
											fullWidth
											variant="outlined"
											type="password"
											name="password"
											error={!!errors.password}
											helperText={errors?.password?.message}
											inputRef={register({
												required: validationMessages.requiredMessage,
												validate: validatePassword,
											})}
										/>
										<Suspense fallback={<Throbber />}>
											<PasswordStrengthBar password={getValues('password')} minLength={8} />
										</Suspense>
										{open ? (
											<div className={classes.passwordRequirement}>
												<Typography variant="h6" className={classes.paperTitle}>
													Password Requirements
												</Typography>
												<Typography className={classes.paper} variant="body2" color="inherit">
													Contains a minimum of 8 characters
												</Typography>
												<Typography className={classes.paper} variant="body2" color="inherit">
													Contains at least one lowercase letter (a–z)
												</Typography>
												<Typography className={classes.paper} variant="body2" color="inherit">
													Contains at least one uppercase letter (A–Z)
												</Typography>
												<Typography className={classes.paper} variant="body2" color="inherit">
													Contains at least one number (0–9)
												</Typography>
												<Typography className={classes.paper} variant="body2" color="inherit">
													Contains at least one special character (!@#$%)
												</Typography>
											</div>
										) : null}
									</div>
								</ClickAwayListener>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Confirm Password"
									fullWidth
									variant="outlined"
									type="password"
									name="password2"
									error={!!errors.password2}
									helperText={errors?.password2?.message}
									inputRef={register({
										required: validationMessages.requiredMessage,
										validate: () =>
											getValues('password') !== getValues('password2')
												? validationMessages.passwordsMatch
												: undefined,
									})}
								/>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Button
								type="submit"
								variant="contained"
								color="primary"
								data-cy="loginButton"
								disabled={
									!!errors.password ||
									!!errors.password2 ||
									getValues('password') !== getValues('password2') ||
									formState.isSubmitting
								} // !! => https://dev.to/sanchithasr/what-is-the-double-bang-operator-in-javascript-4i3h
								fullWidth
							>
								Create Account
							</Button>
						</Grid>
						<Grid item xs={12}>
							<Typography variant="body2">
								By clicking “Create Account”, you acknowledge that you have read, understood, and agree
								to the{' '}
								<InfoLink to="https://viiision.com/user-agreement/">Subscription Agreement</InfoLink>{' '}
								and <InfoLink to="https://viiision.com/privacy-policy/">Privacy Policy.</InfoLink>
							</Typography>
						</Grid>
					</Grid>
					<Grid container item direction="row" xs={12} className={classes.bottomOfForm}>
						<Grid item xs={12} className={classes.centerMyContentPlease}>
							<Typography variant="body2">
								I have an account. <InfoLink to="sign-in">Sign In</InfoLink>
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			</form>
		</>
	);
};
